import { useCallback, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import type { RootState } from '../../store'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { CommentType, GroupMember } from '@cobu/types'
import GroupDiscussion from '../../components/chat/groupDiscussion/GroupDiscussion'
import GroupMembers from '../../components/GroupMembers/GroupMembers'
import { useGroupMembers } from '../../hooks/useGroupMembers'
import { URLS } from '../../constants'
import ChatTooltip from '../../components/chat/ChatTooltip'
import { useChatTooltip } from '../../hooks/useChatTooltips'
import { Button, YStack, Spinner } from 'tamagui'
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline'
import { useChannelList } from '../../hooks/useChannelList'
import { analyticsService, AnalyticsEvent } from '@cobu/analytics'
import { PullToRefreshWrapper } from '../../components/ui/PullToRefreshWrapper'
import { useGroupDiscussion } from '../../hooks/useGroupDiscussion'
import { logger } from '../../utils/logger'
import { CobuText } from '@cobu/ui'

export default function ChatIndex() {
  const buildingId = useSelector((s: RootState) => s.auth.user?.building?.uuid as string | undefined)
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const currentUserId = useSelector((s: RootState) => s.auth.user?.uuid as string | undefined)
  const { tooltip, handleTooltipCtaPress } = useChatTooltip({ buildingId: buildingId || '', enabled: true })
  const { isStreamAvailable, refreshChannels } = useChannelList({ enabled: false })

  // Deep linking parameters
  const commentId = searchParams.get('commentId') || undefined
  const scrollToComment = searchParams.get('scrollToComment') === 'true'

  // Deep linking loading state
  const [isFindingDeepLinkedComment, setIsFindingDeepLinkedComment] = useState(false)

  if (commentId || scrollToComment) {
    console.log('🔍 [Chat Deep Link] Params from URL:', {
      commentId,
      scrollToComment,
      rawQuery: searchParams.toString(),
    });
  }
  
  // Use useGroupDiscussion hook for refresh functionality
  const { refreshComments, refreshing, loading: commentsLoading } = useGroupDiscussion({
    buildingId,
    commentType: CommentType.GroupComment,
  })

  // Combined refresh handler for chat (refreshes comments and channels)
  const handleChatRefresh = useCallback(async () => {
    try {
      await Promise.all([
        refreshComments(),
        ...(isStreamAvailable && refreshChannels ? [refreshChannels()] : [])
      ])
    } catch (error) {
      logger.error('Error refreshing chat:', error)
    }
  }, [refreshComments, isStreamAvailable, refreshChannels])

  const renderDeepLinkFindingOverlay = useCallback(() => {
    if (!isFindingDeepLinkedComment || commentsLoading) return null;
    return (
      <YStack
        ai="center"
        jc="center"
        bc="$blackTransparent50"
        style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 1000 }}
      >
        <YStack
          ai="center"
          jc="center"
          gap="$2"
          p="$4"
          br="$4"
          bc="$white"
          maw={280}
          mx="$4"
        >
          <Spinner size="small" color="#00B4D8" />
          <CobuText variant="body" color="$cobuDarkBlue" fos="$3" ta="center">
            Finding your comment…
          </CobuText>
        </YStack>
      </YStack>
    );
  }, [isFindingDeepLinkedComment]);

  const handleComposeComment = useCallback((replyTo?: any) => {
    /* ANALYTICS_DISABLED: COMPOSE_COMMENT_STARTED ('compose_comment_started')
      analyticsService.trackEvent(AnalyticsEvent.COMPOSE_COMMENT_STARTED, {
            source: 'chat_tab',
            isReply: !!replyTo,
            buildingId,
          })
      */

    const params = new URLSearchParams({ enableMarketplaceModerator: 'true' })
    if (replyTo) params.set('replyingTo', JSON.stringify(replyTo))
    if (buildingId) params.set('buildingId', buildingId)
    navigate(`/chat/compose-comment?${params.toString()}`)
  }, [navigate, buildingId])
  // Fetch members for the building and supply explicitly to GroupMembers
  const { members: rawMembers, isLoading: isMembersLoading } = useGroupMembers({
    buildingId: buildingId || undefined,
    enabled: !!buildingId,
    maxDisplay: 8,
    prioritizePictures: true,
    filterResidentsOnly: true,
  })

  // Normalize and filter like mobile (exclude deactivated/blocked, only residents)
  const filteredMembers = useMemo(() => {
    const toUpper = (s?: string) => (s || '').toUpperCase()
    const isPerkSpotMember = (member: any) => {
      const ln = toUpper(member?.lastName)
      return ln.includes('PERKSPOT') || ln.includes('PERKS AT WORK')
    }
    return (rawMembers || []).filter((m: any) =>
      !m?.deactivated &&
      !m?.blocked &&
      (m?.role?.value === 0 || toUpper(m?.role?.name) === 'RESIDENT') &&
      !isPerkSpotMember(m)
    )
  }, [rawMembers])

  const handleUserProfile = useCallback((userId: string) => {
    navigate(`/public-profile/${userId}`)
  }, [navigate])

  const handleShowAllMembers = useCallback(() => {
    // Pass members via navigation state to avoid URL bloat; All Members will fallback to fetching if absent
    navigate('/chat/all-members', {
      state: {
        members: filteredMembers,
        totalCount: filteredMembers.length,
        currentUserId,
        enableDM: isStreamAvailable,
      },
    })
  }, [navigate, filteredMembers, currentUserId, isStreamAvailable])

  return (
    <PullToRefreshWrapper
      onRefresh={handleChatRefresh}
      isRefreshing={refreshing}
      useWindowScroll={true}
    >
      <>
        {/* DMs button moved to TopTabs right slot */}
        {buildingId && (
          <GroupMembers
            buildingId={buildingId}
            isGlobalChat
            maxDisplay={8}
            members={filteredMembers as any}
            totalCount={filteredMembers.length}
            onShowAllMembers={handleShowAllMembers}
            onMemberPress={(member: GroupMember) => handleUserProfile(member.uuid)}
          />
        )}
        {tooltip && (
          <ChatTooltip tooltip={tooltip} onCtaPress={handleTooltipCtaPress} />
        )}
        <GroupDiscussion
            buildingId={buildingId}
            commentType={CommentType.GroupComment}
            publicChat
            targetCommentId={commentId}
            scrollToComment={scrollToComment}
            onDeepLinkLoadingChange={setIsFindingDeepLinkedComment}
            onComposeComment={handleComposeComment}
            onUserProfile={handleUserProfile}
          />
        {renderDeepLinkFindingOverlay()}
      </>
    </PullToRefreshWrapper>
  )
}


